import { useEffect, useRef, useState } from 'react';
import { Copy, Check, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

// Shared only by the configuration controls; clipboard errors never imply success.
export function useConfigurationCopy(text, label) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');
  const [copying, setCopying] = useState(false);
  const pending = useRef(false);
  const timer = useRef();
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    if (pending.current || !text) return;
    pending.current = true;
    setCopying(true);
    clearTimeout(timer.current);
    setCopied(false);
    setCopyError('');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied`);
      timer.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      const message = 'Copy failed. Select the text and copy it manually, or allow clipboard access and retry.';
      setCopyError(message);
      toast.error(message);
    } finally {
      pending.current = false;
      setCopying(false);
    }
  };
  return { copied, copying, copyError, copy };
}

/* Label row, mono URL with the secret masked by default, copy + reveal. */
export function WebhookEndpoint({ label, url, desc, testid }) {
  const { copied, copying, copyError, copy: handleCopy } = useConfigurationCopy(url, `${label} URL`);
  const [shownUrl, setShownUrl] = useState(null);
  const show = shownUrl === url;

  const masked = url.replace(/([?&]key=)[^&#]*/gi, '$1' + '•'.repeat(14));

  return (
    <div data-testid={testid} className="rounded-md" style={{ border: '1px solid var(--stroke)', backgroundColor: '#ffffff' }}>
      <div className="flex items-center gap-2 px-3 pt-2.5">
        <span className="text-[11px] font-semibold" style={{ color: 'var(--text)' }}>{label}</span>
        {desc && <span className="text-[11px] truncate" style={{ color: 'var(--text-dim)' }}>{desc}</span>}
      </div>
      <div className="flex items-center gap-1.5 px-3 py-2">
        <code
          className="text-[12px] flex-1 min-w-0 break-all rounded px-2.5 py-1.5"
          style={{ color: 'var(--ink)', backgroundColor: 'var(--surface-2)', border: '1px solid var(--stroke-soft)' }}
        >
          POST {show ? url : masked}
        </code>
        {/[?&]key=[^&#]+/i.test(url) && (
          <button type="button" aria-pressed={show} aria-label={`${show ? 'Hide' : 'Show'} ${label} webhook key`} onClick={() => setShownUrl(show ? null : url)} className="shrink-0 p-1.5 rounded-md hover:bg-[rgba(32,33,31,0.05)]" style={{ color: 'var(--text-dim)' }}>
            {show ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
        )}
        <button type="button" disabled={copying || !url || /[?&]key=(?:[&#]|$)/i.test(url)} aria-busy={copying} aria-label={`Copy ${label} webhook URL`} onClick={handleCopy} className="shrink-0 p-1.5 rounded-md hover:bg-[rgba(32,33,31,0.05)]" style={{ color: copied ? 'var(--brand-red)' : 'var(--text-dim)' }}>
          {copied ? <Check size={13} /> : <Copy size={13} />}
        </button>
      </div>
      {copyError && <p role="alert" className="sp-form-error px-3 pb-2">{copyError}</p>}
    </div>
  );
}
